<template>
  <div class="productos-contenedor">
    <h2 class="mb-4">Inventario de Productos</h2>

    <!-- Filtros -->
    <div class="tarjeta mb-4">
      <div class="tarjeta-cuerpo">
        <div class="fila-filtros">
          <div class="filtro-campo">
            <label class="campo-etiqueta">Buscar</label>
            <input v-model="filtroBusqueda" type="text" class="campo-entrada" placeholder="Nombre, código..." />
          </div>
          <div class="filtro-campo filtro-corto">
            <label class="campo-etiqueta">Tipo</label>
            <select v-model="filtroTipo" @change="resetPaginaYCargar" class="campo-seleccion">
              <option value="">Todos</option>
              <option value="bien">Bien</option>
              <option value="servicio">Servicio</option>
            </select>
          </div>
          <div class="filtro-acciones">
            <button @click="limpiarFiltros" class="btn-secundario">
              <i class="bi bi-x-circle me-1"></i> Limpiar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3 d-flex align-items-center gap-2 flex-wrap">
      <router-link to="/inventario/productos/nuevo" class="btn-primario">
        <i class="bi bi-plus-circle me-1"></i> Nuevo Producto
      </router-link>

      <!-- Exportar Excel -->
      <div class="dropdown">
        <button
          class="btn btn-sm btn-outline-success dropdown-toggle"
          type="button"
          @click="menuExportarAbierto = !menuExportarAbierto"
          :disabled="exportando"
        >
          <span v-if="exportando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-file-earmark-excel me-1"></i>
          Exportar Excel
        </button>
        <ul class="dropdown-menu" :class="{ show: menuExportarAbierto }">
          <li>
            <button class="dropdown-item" @click="exportarExcel('todo')">
              <i class="bi bi-table me-2 text-success"></i>
              Todo (Precios, Costos y Stock)
            </button>
          </li>
          <li>
            <button class="dropdown-item" @click="exportarExcel('medio')">
              <i class="bi bi-currency-dollar me-2 text-primary"></i>
              Solo precios 
            </button>
          </li>
          <li>
            <button class="dropdown-item" @click="exportarExcel('stock')">
              <i class="bi bi-boxes me-2 text-warning"></i>
              Solo stock
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="tarjeta">
      <div class="tarjeta-cuerpo">
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border" role="status" style="color: var(--primary-main);">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>
        <div v-else-if="productos.length === 0" class="alerta-info">
          No se encontraron productos registrados.
        </div>
        <template v-else>
          <div class="table-responsive">
            <table class="tabla-datos">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Precio Venta</th>
                  <th>% IVA</th>
                  <th>Total c/IVA</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="producto in productos" :key="producto.id">
                  <td><code class="codigo-texto">{{ producto.codigo_principal }}</code></td>
                  <td>{{ producto.nombre_producto }}</td>
                  <td>
                    <span class="insignia" :class="obtenerClaseTipo(producto.tipo_producto)">
                      {{ obtenerEtiquetaTipo(producto.tipo_producto) }}
                    </span>
                  </td>
                  <td>${{ parseFloat(producto.precio_venta_maximo || producto.precio_unitario || 0).toFixed(2) }}</td>
                  <td>{{ etiquetaIva(producto.tarifa_impuesto) }}</td>
                  <td>${{ calcularTotalConIva(producto).toFixed(2) }}</td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button @click="editarProducto(producto.id)" class="btn-accion btn-accion-editar" title="Editar">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button @click="confirmarEliminar(producto)" class="btn-accion btn-accion-eliminar" title="Eliminar">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Paginación -->
          <div v-if="totalPaginas > 1" class="d-flex justify-content-between align-items-center mt-3 px-1">
            <span class="texto-secundario small">
              Mostrando {{ paginaActual * porPagina + 1 }}–{{ Math.min((paginaActual + 1) * porPagina, totalProductos) }} de {{ totalProductos }}
            </span>
            <div class="d-flex gap-1">
              <button class="btn-paginacion" :disabled="paginaActual === 0" @click="irPagina(0)">
                <i class="bi bi-chevron-double-left"></i>
              </button>
              <button class="btn-paginacion" :disabled="paginaActual === 0" @click="irPagina(paginaActual - 1)">
                <i class="bi bi-chevron-left"></i>
              </button>
              <span class="btn-paginacion btn-paginacion-activo">{{ paginaActual + 1 }} / {{ totalPaginas }}</span>
              <button class="btn-paginacion" :disabled="paginaActual >= totalPaginas - 1" @click="irPagina(paginaActual + 1)">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button class="btn-paginacion" :disabled="paginaActual >= totalPaginas - 1" @click="irPagina(totalPaginas - 1)">
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal Confirmar Eliminar -->
    <div v-if="productoParaEliminar" class="modal-fondo">
      <div class="modal-contenido">
        <div class="modal-encabezado">
          <h5 class="modal-titulo">Confirmar Eliminación</h5>
          <button type="button" class="btn-close" @click="productoParaEliminar = null"></button>
        </div>
        <div class="modal-cuerpo">
          <p>¿Desea eliminar el producto <strong>{{ productoParaEliminar.nombre_producto }}</strong>?</p>
          <p class="texto-secundario small">Esta acción no se puede deshacer.</p>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn-secundario" @click="productoParaEliminar = null">Cancelar</button>
          <button type="button" class="btn-peligro" @click="eliminarProducto">Eliminar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inventarioService } from '@/services/inventarioService';
import notificationService from '@/services/notificationService';
import * as XLSX from 'xlsx';

export default {
  name: 'ClientInventarioProductos',
  data() {
    return {
      productos: [],
      filtroBusqueda: '',
      filtroTipo: '',
      cargando: false,
      productoParaEliminar: null,
      exportando: false,
      menuExportarAbierto: false,
      // Paginación
      paginaActual: 0,
      totalProductos: 0,
      porPagina: 25,
    };
  },
  computed: {
    totalPaginas() {
      return this.totalProductos > 0 ? Math.ceil(this.totalProductos / this.porPagina) : 1;
    },
  },
  watch: {
    filtroBusqueda() {
      // Debounce 400 ms para no lanzar request en cada tecla
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.resetPaginaYCargar(), 400);
    },
  },
  mounted() {
    this.cargarProductos();
    this._cerrarMenuExportar = (e) => {
      if (!this.$el.querySelector('.dropdown')?.contains(e.target)) {
        this.menuExportarAbierto = false;
      }
    };
    document.addEventListener('click', this._cerrarMenuExportar);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._cerrarMenuExportar);
  },
  activated() {
    this.cargarProductos();
  },
  methods: {
    async cargarProductos() {
      this.cargando = true;
      try {
        const filtros = {};
        if (this.filtroBusqueda) filtros.busqueda = this.filtroBusqueda;
        if (this.filtroTipo)     filtros.tipo_producto = this.filtroTipo;

        const respuesta = await inventarioService.getProductos({
          limit:  this.porPagina,
          offset: this.paginaActual * this.porPagina,
          orden:  'nombre_producto',
          dir:    'ASC',
          filtros,
        });
        this.productos      = respuesta?.data || [];
        this.totalProductos = respuesta?.total ?? this.productos.length;
      } catch (error) {
        console.error('Error al cargar productos:', error);
        this.productos = [];
      } finally {
        this.cargando = false;
      }
    },
    resetPaginaYCargar() {
      this.paginaActual = 0;
      this.cargarProductos();
    },
    irPagina(n) {
      this.paginaActual = n;
      this.cargarProductos();
    },
    limpiarFiltros() {
      this.filtroBusqueda = '';
      this.filtroTipo     = '';
      this.paginaActual   = 0;
      this.cargarProductos();
    },
    async exportarExcel(tipo) {
      this.menuExportarAbierto = false;
      this.exportando = true;
      try {
        // Llama al endpoint dedicado que hace JOIN con productos_precios e inventario_stock
        const respuesta = await inventarioService.getProductosExportar(tipo);
        const productos = respuesta?.productos || [];

        const etiquetaIva = (tarifa) => {
          const t = parseFloat(tarifa);
          if (isNaN(t)) return '—';
          if (t === -1) return 'No Objeto';
          if (t === -2) return 'Exento';
          return t + '%';
        };

        const etiquetaTipo = (t) => t === 'bien' ? 'Bien' : 'Servicio';

        let cabeceras, filas;

        if (tipo === 'medio') {
          cabeceras = ['Código Principal', 'Código Auxiliar', 'Nombre Producto',
            'Tipo', 'Categoría', 'Marca', 'Unidad', '% IVA',
            'Precio 1', 'Precio 2', 'Precio 3', 'Precio 4', 'Precio 5'];
          filas = productos.map(p => [
            p.codigo_principal,      p.codigo_auxiliar || '',
            p.nombre_producto,       etiquetaTipo(p.tipo_producto),
            p.categoria,             p.marca,             p.unidad,
            etiquetaIva(p.tarifa_impuesto),
            p.precio1, p.precio2, p.precio3, p.precio4, p.precio5,
          ]);
        } else if (tipo === 'stock') {
          cabeceras = ['Código Principal', 'Código Auxiliar', 'Nombre Producto',
            'Tipo', 'Categoría', 'Marca', 'Unidad',
            'Stock Actual', 'Stock Mínimo', 'Stock Máximo'];
          filas = productos.map(p => [
            p.codigo_principal,      p.codigo_auxiliar || '',
            p.nombre_producto,       etiquetaTipo(p.tipo_producto),
            p.categoria,             p.marca,             p.unidad,
            p.stock_total, p.stock_minimo, p.stock_maximo,
          ]);
        } else {
          // todo
          cabeceras = ['Código Principal', 'Código Auxiliar', 'Nombre Producto',
            'Tipo', 'Categoría', 'Marca', 'Unidad', '% IVA',
            'Costo (Precio Base)',
            'Precio 1', 'Precio 2', 'Precio 3', 'Precio 4', 'Precio 5',
            'Stock Actual', 'Stock Mínimo', 'Stock Máximo'];
          filas = productos.map(p => [
            p.codigo_principal,      p.codigo_auxiliar || '',
            p.nombre_producto,       etiquetaTipo(p.tipo_producto),
            p.categoria,             p.marca,             p.unidad,
            etiquetaIva(p.tarifa_impuesto),
            p.precio_unitario,
            p.precio1, p.precio2, p.precio3, p.precio4, p.precio5,
            p.stock_total, p.stock_minimo, p.stock_maximo,
          ]);
        }

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([cabeceras, ...filas]);
        XLSX.utils.book_append_sheet(wb, ws, 'Productos');

        const labelTipo = { todo: 'completo', medio: 'solo_precios', stock: 'solo_stock' }[tipo];
        XLSX.writeFile(wb, `productos_${labelTipo}_${new Date().toISOString().slice(0, 10)}.xlsx`);

      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || 'Error al exportar';
        notificationService.error(msg);
      } finally {
        this.exportando = false;
        this.menuExportarAbierto = false;
      }
    },
    editarProducto(identificador) {
      this.$router.push(`/inventario/editar/${identificador}`);
    },
    confirmarEliminar(producto) {
      this.productoParaEliminar = producto;
    },
    async eliminarProducto() {
      try {
        await inventarioService.deleteProducto(this.productoParaEliminar.id);
        notificationService.success('Producto eliminado correctamente');
        this.productoParaEliminar = null;
        this.cargarProductos();
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || 'Error al eliminar el producto';
        notificationService.error(msg);
      }
    },
    obtenerEtiquetaTipo(tipo) {
      const etiquetas = { bien: 'Bien', servicio: 'Servicio' };
      return etiquetas[tipo] || tipo;
    },
    obtenerClaseTipo(tipo) {
      const clases = { bien: 'insignia-primaria', servicio: 'insignia-exito' };
      return clases[tipo] || 'insignia-secundaria';
    },
    etiquetaIva(tarifa) {
      const t = parseFloat(tarifa);
      if (isNaN(t)) return '—';
      if (t === -1) return 'No Objeto';
      if (t === -2) return 'Exento';
      return t + '%';
    },
    calcularTotalConIva(producto) {
      const precio = parseFloat(producto.precio_venta_maximo || producto.precio_unitario || 0);
      const tarifa = parseFloat(producto.tarifa_impuesto);
      if (isNaN(tarifa) || tarifa <= 0) return precio;
      return precio * (1 + tarifa / 100);
    }
  }
};
</script>

<style scoped>
.productos-contenedor { padding: 20px; color: var(--text-primary); }
.tarjeta { background-color: var(--secondary-bg-main); border: 1px solid var(--border-main); border-radius: 8px; box-shadow: var(--shadow-sm); }
.tarjeta-cuerpo { padding: 1rem; }
.tabla-datos { width: 100%; border-collapse: collapse; }
.tabla-datos th { background-color: var(--bg-main); color: var(--text-secondary); font-weight: 600; padding: 0.75rem; border-bottom: 1px solid var(--border-main); text-align: left; }
.tabla-datos td { padding: 0.75rem; border-bottom: 1px solid var(--border-main); color: var(--text-primary); }
.tabla-datos tr:hover td { background-color: var(--bg-main); }
.alerta-info { padding: 1rem; background-color: var(--bg-main); border: 1px solid var(--border-main); border-radius: 6px; color: var(--text-secondary); }
.fila-filtros { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; }
.filtro-campo { flex: 1; min-width: 180px; }
.filtro-corto { flex: 0 0 160px; }
.filtro-acciones { display: flex; align-items: flex-end; }
.insignia { display: inline-block; padding: 0.25rem 0.5rem; border-radius: 4px; font-size: 0.8rem; font-weight: 500; }
.insignia-primaria { background-color: var(--primary-main); color: white; }
.insignia-exito { background-color: var(--accent-success); color: white; }
.insignia-secundaria { background-color: var(--text-muted); color: white; }
.codigo-texto { color: var(--primary-main); font-size: 0.9rem; }
.texto-secundario { color: var(--text-secondary); }
.btn-primario { background-color: var(--primary-main); color: white; border: none; padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; text-decoration: none; display: inline-block; }
.btn-primario:hover { background-color: var(--primary-hover); }
.btn-secundario { background-color: var(--bg-main); color: var(--text-primary); border: 1px solid var(--border-main); padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; }
.btn-peligro { background-color: var(--accent-danger); color: white; border: none; padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer; }
.btn-accion { border: none; padding: 0.25rem 0.5rem; border-radius: 4px; cursor: pointer; margin: 0 2px; }
.btn-accion-editar { background-color: var(--primary-main); color: white; }
.btn-accion-eliminar { background-color: var(--accent-danger); color: white; }
.btn-accion:hover { opacity: 0.85; }
.modal-fondo { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.modal-contenido { background-color: var(--secondary-bg-main); border: 1px solid var(--border-main); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: var(--shadow-md); }
.modal-encabezado { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border-main); }
.modal-titulo { margin: 0; color: var(--text-primary); font-size: 1.1rem; }
.modal-cuerpo { padding: 1.25rem; }
.modal-pie { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 1rem 1.25rem; border-top: 1px solid var(--border-main); }
.campo-etiqueta { display: block; margin-bottom: 0.4rem; color: var(--text-secondary); font-size: 0.9rem; }
.campo-entrada, .campo-seleccion { width: 100%; padding: 0.5rem 0.75rem; background-color: var(--bg-main); border: 1px solid var(--border-main); border-radius: 6px; color: var(--text-primary); font-size: 0.95rem; box-sizing: border-box; }
.campo-entrada:focus, .campo-seleccion:focus { outline: none; border-color: var(--border-focus); }
/* Paginación */
.btn-paginacion { background-color: var(--bg-main); color: var(--text-primary); border: 1px solid var(--border-main); padding: 0.3rem 0.6rem; border-radius: 4px; cursor: pointer; font-size: 0.85rem; }
.btn-paginacion:hover:not(:disabled) { background-color: var(--primary-main); color: white; border-color: var(--primary-main); }
.btn-paginacion:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-paginacion-activo { background-color: var(--primary-main); color: white; border-color: var(--primary-main); cursor: default; }
</style>